/* eslint-disable max-len */
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { DealerType } from '@1po/1po-bff-fe-spec/generated/user/model/CommercialLink';
import { ROUTER_ESTIMATE } from 'app/AppRouter';
import { RootState } from 'app/AppStore';
import { trackAppEvent } from 'app/AppTracker';
import { IndustrySmallIcon } from 'assets/icons';
import { AddToEstimateButtonAndDialog } from 'components/AddToEstimate/AddToEstimateButtonAndDialog';
import { CopyReferenceWithFeedback, CopySelectionWrapper } from 'components/CopyReferenceFeedback';
import { DataContainer, EMPTY_FRAGMENT, ErrorWithLabel } from 'components/DataContainer';
import { DocumentationAlertButton } from 'components/DocumentationAlertButton';
import { BANNER_HEIGHT } from 'components/Page/Header/AdvertisementSpace/InformationBanner';
import { HEADER_HEIGHT } from 'components/Page/Header/TopHeader/TopHeader';
import OrderActionsSection from 'components/ReferencePriceSection/OrderActionsSection';
import { PriceErrorDisplay } from 'components/ReferencePriceSection/PriceErrorDisplay';
import PriceSection from 'components/ReferencePriceSection/PriceSection';
import { PriceUnavailable } from 'components/ReferencePriceSection/PriceUnavailable';
import {
  ReferenceUnavailable,
  ReferenceUnavailableForOrder,
} from 'components/ReferenceUnavailableBox/ReferenceUnavailable';
import StockDisplay from 'components/StockInfo';
import TireBasketPriceInfo from 'components/TireBasketPriceInfo';
import TiresRatingIcons from 'components/TireIcons/TireRatingIcons';
import { addOtherSectionReferenceRequest } from 'domains/basket/Basket.store';
import {
  addReferenceFromCatalogToCart,
  getLastSearchedVehicleKey,
  getSearchVehicleResult,
} from 'domains/catalog/Catalog.store';
import { DATAHUB, LIFECYCLE_W3, LIFECYCLE_W4, PRODUCT_MENU_TABS, STANDARD, TIRE } from 'domains/catalog/Catalog.types';
import { convertReferenceToEstimateReference, convertTireToEstimateTire } from 'domains/estimate/Estimate.mapper';
import { addCatalogReference, addCatalogTire } from 'domains/estimate/Estimate.store';
import {
  getIsStockAvailable,
  getPrice,
  getStockInfo,
  ReferencePriceType,
  setSelectedTab as setSelectedTabMenu,
} from 'domains/references';
import {
  useFetchDiscontinuedReferences,
  useFetchPlatesForReference,
  useFetchPrices,
  useFetchRepairPrices,
  useFetchReuseAvailabilities,
  useFetchReuseStocks,
} from 'domains/references/References.requests';
import { getDealerType, getUserCountry, getUserRights, UserRole } from 'domains/user';
import { SPopover } from 'pages/CatalogPage/DH/Product/ProductDescription.styled';
import ElectronicPartsRepairLink from 'pages/CatalogPage/DH/SubcategorySection/SparePartsSection/ReferenceCardsContainer/ReferenceCard/ElectronicPartsRepairLink';
import Reuse from 'pages/CatalogPage/DH/SubcategorySection/SparePartsSection/ReferenceCardsContainer/ReferenceCard/Reuse';
import ReuseAvailabilitiesButton from 'pages/CatalogPage/DH/SubcategorySection/SparePartsSection/ReferenceCardsContainer/ReferenceCard/ReuseAvailabilities/ReuseAvailabilitiesButton';
import { useMyStoreTireDiscount } from 'pages/MyStorePage/useMyStoreTireDiscount';
import { theme } from 'styles';
import {
  Box,
  Bubble,
  Flex,
  Icon,
  LinkRoundButton,
  Lowercase,
  MarginBox,
  NotificationLink,
  notifyTop,
  Spin,
  Text,
  WarningAlert,
  WhiteButton,
} from 'UI';
import { FOUND, getData, getTractionBatteryOrderStatus, isLoading, LOADING, NOT_FOUND, SEARCH_STATUS } from 'utils';
import { TRACKING_EVENT_ADD_TO_ESTIMATE, TRACKING_EVENT_GO_TO_ESTIMATE } from 'utils/eventTracker/EventTracker.types';
import { OFFSET_CATALOG, useOffsetTop } from 'utils/hooks/useOffsetTop';
import { scrollToTopSmooth } from 'utils/hooks/useResetScroll';
import { Country } from 'utils/i18n/Country';
import { Language, webLanguages } from 'utils/i18n/Language';
import { ProductDetailProps } from './ProductTab';

const getProductDesignationTextType = (availableInUserCountry: boolean) => {
  return availableInUserCountry ? 'h1_banner_light' : 'h1_banner_black_45';
};

/**
 * ProductDescription component displays information such as Product title, price and stocks
 */

const renderPriceError = () => <PriceErrorDisplay />;
const renderPriceLoading = () => <Spin size={'small'} />;

const getDocumentationDataStatus = (isPlateLoading: boolean, hasContentToDisplay: boolean): SEARCH_STATUS => {
  if (isPlateLoading) {
    return LOADING;
  }
  return hasContentToDisplay ? FOUND : NOT_FOUND;
};

export const ProductDocumentationSection = ({
  seePlate,
  isApplicableToCurrentVehicle,
  referenceNumber,
  vehicleKey,
}: {
  seePlate?: React.ReactNode;
  isApplicableToCurrentVehicle?: boolean;
  referenceNumber: string;
  vehicleKey: string | undefined;
}) => {
  const { t } = useTranslation();
  const { searchStatus, data: plateLines } = useFetchPlatesForReference(referenceNumber, vehicleKey);

  const hasSeePlateData = !!plateLines?.length;
  const isIncompatibleWithVehicle = isApplicableToCurrentVehicle === false;
  const dataStatus = getDocumentationDataStatus(isLoading(searchStatus), hasSeePlateData || isIncompatibleWithVehicle);

  return (
    <DataContainer data={dataStatus} NotFound={EMPTY_FRAGMENT}>
      <MarginBox mt={20}>
        <Flex direction={'column'} gap={16} align={'flex-start'}>
          <Text type={'h5_bold'}>{t('catalog.product.documentation', 'Documentation')}</Text>
          {seePlate && <Flex size={0}>{seePlate}</Flex>}
          {isIncompatibleWithVehicle && (
            <Flex size={0}>
              <ErrorWithLabel
                label={t('catalog.reference.incompatible_vehicle', 'Part non-compatible with selected vehicle.')}
                narrow
                color={theme.color.palette_red_7}
              />
            </Flex>
          )}
        </Flex>
      </MarginBox>
    </DataContainer>
  );
};

export const ProductDescription = ({
  refLocal,
  sparePartsView,
  seePlate,
  isVehicleCatalog,
  isUniversalProduct,
  isAvailableSoon,
  isNoLongerAvailable,
  isPopin,
  isCrossSellReference,
}: ProductDetailProps) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    referenceNumber,
    name,
    commercialName,
    brand,
    imageKeys,
    reach,
    type,
    isApplicableToCurrentVehicle,
    tireDetails,
  } = refLocal;
  const vehicleKey = useSelector(getLastSearchedVehicleKey);
  const vehicle = useSelector((state: RootState) => getSearchVehicleResult(state, vehicleKey));
  const priceSearchData = useSelector((state: RootState) => getPrice(state, referenceNumber));
  const price = getData(priceSearchData);
  const stock = useSelector((state: RootState) => getStockInfo(state, referenceNumber));
  const stockSearchStatus = stock?.searchStatus;
  const warehouses = stock?.data?.warehouses ?? [];
  const hasStockAtR1 = warehouses.some((w) => w.type === 'LOCAL' && w.confirmedQuantity > 0);
  const country = useSelector(getUserCountry);
  const userCountryName = React.useMemo(() => {
    const websiteLocale = Language.findByKey(webLanguages, i18n.language)?.locale ?? i18n.language;
    return Country.findByKey(country)?.getLocalizedName(websiteLocale) ?? country ?? '';
  }, [country, i18n.language]);
  const offsetTop = useOffsetTop('RENAULT_CATALOG');
  const displaySupersessionLink =
    refLocal.supersessionChainReferences && refLocal.supersessionChainReferences.length > 0;
  const dealerType = useSelector(getDealerType);
  const allowedDealerTypes: DealerType[] = [UserRole.R1, UserRole.R2, UserRole.R3_MOTRIO];
  const tireW3WithoutR1 =
    refLocal.type === TIRE &&
    refLocal.tireDetails?.lifeCycle === LIFECYCLE_W3 &&
    stockSearchStatus === FOUND &&
    !hasStockAtR1;
  const vehicleDetail = vehicle?.vehicleDetail;
  const userRoles = useSelector(getUserRights);
  const resolvedUserRoles = Array.isArray(userRoles) ? userRoles : undefined;
  const tireNotAvailable = type === TIRE && refLocal.tireDetails?.lifeCycle === LIFECYCLE_W4;
  const availableInUserCountry =
    useSelector((state: RootState) => getIsStockAvailable(state, referenceNumber)) &&
    !tireNotAvailable &&
    !tireW3WithoutR1;
  useFetchRepairPrices([referenceNumber], vehicleKey);
  useFetchReuseStocks([referenceNumber]);
  useFetchReuseAvailabilities([referenceNumber]);
  const discontinued = useFetchDiscontinuedReferences(referenceNumber);
  const isDiscontinued = discontinued?.data ?? false;
  const myStoreTireDiscount = useMyStoreTireDiscount(tireDetails?.brandName, tireDetails?.baseDiameter);
  const referencePriceVehicleKey = isUniversalProduct ? undefined : vehicleKey;
  const hasPrice = !!price;
  const canDisplayProductActions = availableInUserCountry && hasPrice;
  const { orderBlocked, orderBlockedMessage: orderBlockedMessageConfig } = getTractionBatteryOrderStatus({
    userRoles: resolvedUserRoles,
    familyCode: refLocal.familyCode,
    vin: vehicleDetail?.vin,
    isCompatible: isApplicableToCurrentVehicle,
  });
  const orderBlockedMessage = orderBlockedMessageConfig
    ? t(orderBlockedMessageConfig.key, orderBlockedMessageConfig.defaultMessage)
    : undefined;
  const canDisplayAddToEstimate =
    canDisplayProductActions && !isNoLongerAvailable && !isAvailableSoon && !!price?.garageView?.vatExcludedPrice;
  const renderPriceNotFound = React.useCallback(() => <PriceUnavailable hasStockAtR1={hasStockAtR1} />, [hasStockAtR1]);

  useFetchPrices([referenceNumber], referencePriceVehicleKey);

  const handleAddToCartClick = () => {
    if (price?.garageView?.vatExcludedPrice && price.garageView?.vatIncludedPrice) {
      if (
        vehicleKey &&
        vehicleDetail &&
        isApplicableToCurrentVehicle &&
        vehicleDetail.catalogSource === 'DATAHUB' &&
        !isUniversalProduct
      ) {
        dispatch(addReferenceFromCatalogToCart({ referenceNumber, isCrossSellReference, isFromProductPage: true }));
      } else {
        dispatch(
          addOtherSectionReferenceRequest({
            reference: { ...refLocal, isCrossSelling: isCrossSellReference, isFromProductPage: true },
          }),
        );
      }
      setTimeout(() => {
        const crossReferences = document.getElementById('product_cross_selling');
        if (crossReferences) {
          scrollToTopSmooth(crossReferences.offsetTop - HEADER_HEIGHT - BANNER_HEIGHT);
        }
      }, 50);
    }
  };

  const addNotification = (quantity: number) => {
    notifyTop(
      'success',
      refLocal.linkedReferences && refLocal.linkedReferences.length > 0 ? (
        <Trans
          count={quantity}
          i18nKey={'catalog.reference_card.added_with_linked_references_to_estimate.description.v2'}
        >
          {'{{ count }} Reference has been added to your estimate with linked references.'}
        </Trans>
      ) : (
        <Trans count={quantity} i18nKey={'catalog.reference_card.added_to_estimate.description.v2'}>
          {'{{ count }} Reference has been added to your estimate.'}
        </Trans>
      ),
      undefined,
      <NotificationLink
        onClick={() => {
          trackAppEvent(TRACKING_EVENT_GO_TO_ESTIMATE);
          history.push(ROUTER_ESTIMATE);
        }}
      >
        <Trans i18nKey={'catalog.parts.category.car_parts.labor_time.notification.see_estimate'}>
          {'See estimate'}
        </Trans>
      </NotificationLink>,
    );
  };

  const dispatchAddToEstimate = (quantity: number, refPrice: ReferencePriceType) => {
    if (type === STANDARD) {
      const imageUrl = imageKeys && imageKeys.length > 0 ? imageKeys[0] : undefined;
      dispatch(
        addCatalogReference({
          reference: convertReferenceToEstimateReference(
            referenceNumber,
            name ?? '',
            refPrice,
            isApplicableToCurrentVehicle ?? false,
            'DATAHUB',
            brand,
            undefined,
            undefined,
            undefined,
            undefined,
            refLocal.linkedReferences,
            refLocal.familyCode,
            imageUrl,
            quantity,
          ),
        }),
      );
      return;
    }
    if (refLocal.tireDetails) {
      const { profile, brandId, eprelUrl, designation, baseDiameter } = refLocal.tireDetails;
      dispatch(
        addCatalogTire({
          reference: convertTireToEstimateTire(
            refLocal.referenceNumber,
            profile ?? '',
            refPrice,
            true,
            quantity,
            String(baseDiameter),
            brandId,
            eprelUrl,
            designation,
            refLocal.familyCode,
          ),
        }),
      );
    }
  };

  const handleAddToEstimateClick = (quantity = 1) => {
    if (price?.garageView?.vatExcludedPrice && (!isVehicleCatalog || vehicleDetail?.dataHubVehicle)) {
      if (!orderBlocked) {
        addNotification(quantity);
      }
      trackAppEvent(TRACKING_EVENT_ADD_TO_ESTIMATE);
      dispatchAddToEstimate(quantity, price);
    }
  };

  const handleSubstitutionScroll = () => {
    const element = document.getElementById(PRODUCT_MENU_TABS);
    if (element) {
      dispatch(setSelectedTabMenu('substitute_references'));
      if (isPopin) {
        element.scrollIntoView({ behavior: 'smooth' });
      } else {
        scrollToTopSmooth(element.offsetTop - offsetTop - OFFSET_CATALOG);
      }
    }
  };

  return (
    <Flex direction={'column'}>
      <Box>
        <Flex align={'center'} gap={10}>
          <CopySelectionWrapper referenceNumber={referenceNumber}>
            <Text type="base" color="grey55" data-cy={'reference-number'}>
              {referenceNumber}
            </Text>
          </CopySelectionWrapper>
          <CopyReferenceWithFeedback referenceNumber={referenceNumber} />
          {displaySupersessionLink && <LinkRoundButton onClick={handleSubstitutionScroll} />}
          {(refLocal.brand === 'MOTRIO' || refLocal.brand === 'MOTRIO_PNEU') && (
            <DocumentationAlertButton
              reference={refLocal}
              vehicleDetail={vehicleDetail}
              vehicleRegistrationNumber={vehicleDetail?.vrn ?? vehicleDetail?.vehicleKey}
              inverted
              size={12}
              boxSize={'xsm'}
            />
          )}
        </Flex>
      </Box>
      <Flex align={'center'} size={0}>
        <MarginBox my={5}>
          <Text
            type={getProductDesignationTextType(availableInUserCountry)}
            data-cy={'product-designation'}
            disableGutter
          >
            {name}
          </Text>
        </MarginBox>
      </Flex>
      <Flex size={0} align={'center'}>
        {commercialName && (
          <Text type="lg" disableGutter>
            {commercialName}
          </Text>
        )}
      </Flex>
      {isDiscontinued && (
        <Flex size={0}>
          <Flex align={'center'} data-cy={'product-discontinued'}>
            <Icon
              IconComponent={IndustrySmallIcon}
              size={30}
              background={theme.color.white}
              color={theme.color.red}
              round
              noPointer
            />
            <Text type="base" color="red">
              {t('catalog.product.status.discontinued', 'Discontinued:')}
            </Text>
            &nbsp;
            <Text type="base">
              {t('catalog.product.description.discontinued', 'This product is no longer sold by the manufacturer')}
            </Text>
          </Flex>
        </Flex>
      )}
      {reach && (
        <Flex size={0}>
          <Box height={10} />
          <SPopover
            trigger={'hover'}
            content={
              <Flex>
                <MarginBox mx={15} my={10}>
                  <Text type="base" color="white">
                    {reach}
                  </Text>
                </MarginBox>
              </Flex>
            }
            placement={'right'}
            color={theme.color.grey00}
            arrowPointAtCenter={true}
          >
            <Bubble disabled={!availableInUserCountry}>{t('catalog.product_header.reach', 'REACH')}</Bubble>
          </SPopover>
        </Flex>
      )}
      {tireDetails && (
        <Flex direction={'column'}>
          <Text
            type="base"
            bold={!availableInUserCountry}
            color={availableInUserCountry ? 'grey35' : 'grey55'}
            transform={'capitalize'}
          >
            <Lowercase>{tireDetails.profile}</Lowercase>
          </Text>
          <Text type="base" bold={!availableInUserCountry} color="grey55" transform={'capitalize'}>
            {t('catalog.tires.reference_card.brand', 'Brand:')} <Lowercase>{tireDetails.brandName}</Lowercase>
          </Text>
          <Text
            type="base"
            bold={!availableInUserCountry}
            color={availableInUserCountry ? 'grey35' : 'grey55'}
            transform={'capitalize'}
          >
            <Lowercase>
              {t('catalog.tires.reference_card.characteristics', 'Characteristics:')} {tireDetails.designation}
            </Lowercase>
          </Text>
          <MarginBox mt={10} />
          <TiresRatingIcons
            availableInUserCountry={availableInUserCountry}
            fuelConsumption={tireDetails.fuelConsumption}
            wetGrip={tireDetails.wetGrip}
            exteriorSound={tireDetails.exteriorSound}
          />
        </Flex>
      )}
      <MarginBox mt={25}>
        {availableInUserCountry ? (
          <DataContainer
            data={priceSearchData}
            NotFound={renderPriceNotFound}
            ErrorState={renderPriceError}
            Loading={renderPriceLoading}
          >
            <PriceSection
              referenceNumber={refLocal.referenceNumber}
              sparePartsView={sparePartsView}
              align={'left'}
              referenceType={type}
              clientDiscount={myStoreTireDiscount?.discount}
              isAvailableSoon={isAvailableSoon}
              isNoLongerAvailable={isNoLongerAvailable || isDiscontinued}
              extendedDiscountView
            />
          </DataContainer>
        ) : (
          <WarningAlert
            message={
              <Text type={'h6'}>
                {t('catalog.product.not_sold_in_country.title', 'Product is not sold in {{userCountry}}', {
                  userCountry: userCountryName,
                })}
              </Text>
            }
            description={t(
              'catalog.product.not_sold_in_country.description',
              'This product is not part of the standard distribution catalogue for your country.',
            )}
          />
        )}
      </MarginBox>
      {type === TIRE && hasPrice && (
        <MarginBox mt={5}>
          <TireBasketPriceInfo
            referenceNumber={refLocal.referenceNumber}
            sparePartsView={sparePartsView}
            clientDiscount={myStoreTireDiscount?.discount}
          />
        </MarginBox>
      )}
      <MarginBox mt={25}>
        {availableInUserCountry && !isDiscontinued && (
          <StockDisplay
            vehicleKey={vehicleDetail?.vehicleKey}
            referenceNumber={referenceNumber}
            isApplicableToCurrentVehicle={isApplicableToCurrentVehicle && vehicleDetail?.catalogSource === 'DATAHUB'}
          />
        )}
      </MarginBox>
      {country === Country.FR.key && dealerType && allowedDealerTypes.includes(dealerType) && type !== TIRE && (
        <Reuse referenceNumber={referenceNumber} />
      )}
      {type !== TIRE && <ReuseAvailabilitiesButton referenceNumber={referenceNumber} />}
      <MarginBox mt={15}>
        {availableInUserCountry && <ElectronicPartsRepairLink referenceNumber={referenceNumber} type={'text'} />}
        {hasPrice && (
          <MarginBox mt={30}>
            {availableInUserCountry ? (
              <Flex direction={'column'} gap={12}>
                {orderBlocked && <ReferenceUnavailableForOrder enabled message={orderBlockedMessage} />}
                <OrderActionsSection
                  referenceNumber={refLocal.referenceNumber}
                  referenceType={type}
                  catalogSource={DATAHUB}
                  handleAddToCartClick={handleAddToCartClick}
                  vehicleKey={referencePriceVehicleKey}
                  isApplicableToCurrentVehicle={
                    isApplicableToCurrentVehicle && vehicleDetail?.catalogSource === 'DATAHUB'
                  }
                  availableForOrder={canDisplayProductActions && !orderBlocked}
                  isAvailableSoon={isAvailableSoon}
                  isNoLongerAvailable={isNoLongerAvailable || isDiscontinued}
                  buttonSize={'large'}
                  stretchCartButton
                  centerQuantityModule
                />
                {canDisplayAddToEstimate && (
                  <AddToEstimateButtonAndDialog
                    handleAddToEstimateClick={handleAddToEstimateClick}
                    isVehicleCatalog={isVehicleCatalog}
                    buttonComponent={
                      <WhiteButton stretch={true}>
                        <Flex gap={8} align={'center'} justify={'center'}>
                          {t('catalog.action.add_to_estimate', 'Add to estimate')}
                        </Flex>
                      </WhiteButton>
                    }
                  />
                )}
              </Flex>
            ) : (
              <ReferenceUnavailable size={'medium'} />
            )}
          </MarginBox>
        )}
      </MarginBox>
      <ProductDocumentationSection
        seePlate={seePlate}
        isApplicableToCurrentVehicle={refLocal.isApplicableToCurrentVehicle}
        referenceNumber={refLocal.referenceNumber}
        vehicleKey={vehicleKey}
      />
      <Flex align={'flex-end'} />
    </Flex>
  );
};
